import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import * as invitesApi from '@/api/invites'
import * as usersApi from '@/api/users'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'customer', label: 'Customer' },
]

/** Memorable-enough temporary password for admin resets (min 8 chars). */
function generateTemporaryPassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
  const bytes = new Uint8Array(12)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

function emptyForm() {
  return {
    email: '',
    first_name: '',
    surname: '',
    password: '',
    role: 'customer',
    customer_ids: [],
    can_edit: false,
    can_view_secrets: false,
    can_use_ai_search: false,
  }
}

function emptyInviteForm() {
  return {
    email: '',
    first_name: '',
    surname: '',
    role: 'customer',
    customer_ids: [],
    can_edit: false,
    can_view_secrets: false,
    can_use_ai_search: false,
    expires_in_days: 7,
  }
}

function CustomerPicker({ customers, selectedIds, onChange, disabled }) {
  function toggle(id) {
    const n = Number(id)
    if (selectedIds.includes(n)) {
      onChange(selectedIds.filter((x) => x !== n))
    } else {
      onChange([...selectedIds, n])
    }
  }
  return (
    <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-slate-300 bg-white p-2">
      {customers.length === 0 && (
        <p className="text-xs text-brand-muted">No customers available.</p>
      )}
      {customers.map((c) => (
        <label key={c.id} className="flex items-center gap-2 text-sm text-brand">
          <input
            type="checkbox"
            disabled={disabled}
            checked={selectedIds.includes(c.id)}
            onChange={() => toggle(c.id)}
          />
          {c.name}
        </label>
      ))}
    </div>
  )
}

export function UsersAdmin() {
  const { isAdmin, user: me } = useAuth()
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [inviteForm, setInviteForm] = useState(emptyInviteForm)
  const [invites, setInvites] = useState([])
  const [inviteLink, setInviteLink] = useState(null)
  const [editId, setEditId] = useState(null)
  const [edit, setEdit] = useState(null)
  const [resetUser, setResetUser] = useState(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetDone, setResetDone] = useState(null)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [u, c, inv] = await Promise.all([
          usersApi.listUsers(),
          customersApi.listCustomers(),
          invitesApi.listInvites(),
        ])
        if (!cancelled) {
          setUsers(u)
          setCustomers(c)
          setInvites(inv)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load users.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }
  function fail(err, fallback) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string') setError(detail)
    else if (Array.isArray(detail)) {
      setError(detail.map((d) => d.msg || JSON.stringify(d)).join('; '))
    } else setError(fallback)
  }

  function customerName(id) {
    if (id == null) return '—'
    return customers.find((c) => c.id === id)?.name || `Customer #${id}`
  }

  function customerNames(ids) {
    if (!ids?.length) return '—'
    return ids.map((id) => customerName(id)).join(', ')
  }

  async function handleInvite(e) {
    e.preventDefault()
    if (inviteForm.role === 'customer' && inviteForm.customer_ids.length === 0) {
      setError('Choose at least one customer for a customer invite.')
      return
    }
    setBusy(true)
    setError('')
    setInviteLink(null)
    try {
      const created = await invitesApi.createInvite({
        email: inviteForm.email.trim(),
        first_name: inviteForm.first_name.trim() || undefined,
        surname: inviteForm.surname.trim() || undefined,
        role: inviteForm.role,
        customer_ids: inviteForm.role === 'customer' ? inviteForm.customer_ids : [],
        can_edit: inviteForm.role === 'admin' ? true : inviteForm.can_edit,
        can_view_secrets:
          inviteForm.role === 'admin' ? true : inviteForm.can_view_secrets,
        can_use_ai_search:
          inviteForm.role === 'admin' ? true : inviteForm.can_use_ai_search,
        expires_in_days: Number(inviteForm.expires_in_days) || 7,
      })
      setInviteLink(created.invite_url)
      setInviteForm(emptyInviteForm())
      const inv = await invitesApi.listInvites()
      setInvites(inv)
    } catch (err) {
      fail(err, 'Could not create invite.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRegenerateInvite(id) {
    setBusy(true)
    setError('')
    try {
      const created = await invitesApi.regenerateInvite(id)
      setInviteLink(created.invite_url)
      const inv = await invitesApi.listInvites()
      setInvites(inv)
    } catch (err) {
      fail(err, 'Could not create a new invite link.')
    } finally {
      setBusy(false)
    }
  }

  async function handleRevokeInvite(id) {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return
    setBusy(true)
    setError('')
    try {
      await invitesApi.revokeInvite(id)
      if (inviteLink) setInviteLink(null)
      const inv = await invitesApi.listInvites()
      setInvites(inv)
    } catch (err) {
      fail(err, 'Could not revoke invite.')
    } finally {
      setBusy(false)
    }
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (busy) return
    if (form.role === 'customer' && form.customer_ids.length === 0) {
      setError('Select at least one customer for a customer user.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const body = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        surname: form.surname.trim(),
        password: form.password,
        role: form.role,
        can_edit: form.role === 'admin' ? true : form.can_edit,
        can_view_secrets: form.role === 'admin' ? true : form.can_view_secrets,
        can_use_ai_search:
          form.role === 'admin' ? true : form.can_use_ai_search,
        customer_ids: form.role === 'customer' ? form.customer_ids : [],
      }
      const created = await usersApi.createUser(body)
      setUsers((prev) =>
        [...prev, created].sort((a, b) => a.email.localeCompare(b.email)),
      )
      setForm(emptyForm())
    } catch (err) {
      fail(err, 'Could not create user.')
    } finally {
      setBusy(false)
    }
  }

  function startEdit(u) {
    setEditId(u.id)
    setEdit({
      first_name: u.first_name,
      surname: u.surname,
      role: u.role,
      customer_ids: u.customer_ids?.length
        ? [...u.customer_ids]
        : u.customer_id != null
          ? [u.customer_id]
          : [],
      can_edit: u.can_edit,
      can_view_secrets: u.can_view_secrets,
      can_use_ai_search: u.can_use_ai_search,
      is_active: u.is_active,
      password: '',
    })
  }

  async function handleSaveEdit(e) {
    e.preventDefault()
    if (!editId || busy) return
    if (edit.role === 'customer' && edit.customer_ids.length === 0) {
      setError('Select at least one customer for a customer user.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const body = {
        first_name: edit.first_name.trim(),
        surname: edit.surname.trim(),
        role: edit.role,
        can_edit: edit.role === 'admin' ? true : edit.can_edit,
        can_view_secrets: edit.role === 'admin' ? true : edit.can_view_secrets,
        can_use_ai_search:
          edit.role === 'admin' ? true : edit.can_use_ai_search,
        is_active: edit.is_active,
        customer_ids: edit.role === 'customer' ? edit.customer_ids : [],
      }
      if (edit.password.trim()) body.password = edit.password
      const updated = await usersApi.updateUser(editId, body)
      setUsers((prev) =>
        prev
          .map((u) => (u.id === editId ? updated : u))
          .sort((a, b) => a.email.localeCompare(b.email)),
      )
      setEditId(null)
      setEdit(null)
    } catch (err) {
      fail(err, 'Could not update user.')
    } finally {
      setBusy(false)
    }
  }

  function startResetPassword(u) {
    setEditId(null)
    setEdit(null)
    setResetDone(null)
    setResetUser(u)
    setResetPassword(generateTemporaryPassword())
    setError('')
  }

  function cancelResetPassword() {
    setResetUser(null)
    setResetPassword('')
  }

  async function confirmResetPassword(e) {
    e?.preventDefault?.()
    if (!resetUser || busy) return
    const pwd = resetPassword.trim()
    if (pwd.length < 8) {
      setError('Temporary password must be at least 8 characters.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(resetUser.id, { password: pwd })
      setUsers((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      setResetDone({
        email: updated.email,
        full_name: updated.full_name,
        password: pwd,
      })
      setResetUser(null)
      setResetPassword('')
    } catch (err) {
      fail(err, 'Could not reset password.')
    } finally {
      setBusy(false)
    }
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      setError('Could not copy to clipboard — select the password and copy manually.')
    }
  }

  async function toggleActive(u) {
    if (busy || u.id === me?.id) return
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, { is_active: !u.is_active })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not update user.')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(u) {
    if (busy || u.id === me?.id) return
    if (
      !window.confirm(
        `Delete user ${u.email}? This cannot be undone.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await usersApi.deleteUser(u.id)
      setUsers((prev) => prev.filter((row) => row.id !== u.id))
      if (editId === u.id) {
        setEditId(null)
        setEdit(null)
      }
    } catch (err) {
      fail(err, 'Could not delete user.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleCanEdit(u) {
    if (busy || u.role === 'admin') return
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, { can_edit: !u.can_edit })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not update edit rights.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleCanViewSecrets(u) {
    if (busy || u.role === 'admin') return
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, {
        can_view_secrets: !u.can_view_secrets,
      })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not update password visibility.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleCanUseAiSearch(u) {
    if (busy || u.role === 'admin') return
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, {
        can_use_ai_search: !u.can_use_ai_search,
      })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not update AI Search access.')
    } finally {
      setBusy(false)
    }
  }

  async function turnOff2fa(u) {
    if (busy) return
    if (
      !window.confirm(
        `Turn off 2FA for ${u.email}? They can sign in with password only (useful for testing as that user).`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, {
        totp_enabled: false,
        mfa_exempt: true,
      })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not turn off 2FA.')
    } finally {
      setBusy(false)
    }
  }

  async function require2faAgain(u) {
    if (busy) return
    if (
      !window.confirm(
        `Require 2FA again for ${u.email}? They will be asked to enrol an authenticator if policy requires it.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    try {
      const updated = await usersApi.updateUser(u.id, { mfa_exempt: false })
      setUsers((prev) => prev.map((row) => (row.id === u.id ? updated : row)))
    } catch (err) {
      fail(err, 'Could not update 2FA requirement.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsShell title="Users">
      <div>
          <h1 className="font-display text-4xl tracking-tight">Users</h1>
          <p className="mt-2 text-brand-muted">
            Prefer <strong>Invite user</strong> — enter their email, customers and
            access, then copy the link to send them. They choose their own password.
            You can still create an account with a temporary password if needed.
            Use <strong>Reset password</strong> when someone is locked out. Under
            2FA, use <strong>Turn off</strong> / <strong>Require again</strong> for
            testing.
          </p>

          {error && (
            <p className="mt-4 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          {inviteLink && (
            <div
              className="mt-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950"
              role="status"
            >
              <p className="font-semibold">Invite link ready — copy and send it</p>
              <p className="mt-2 break-all font-mono text-xs">{inviteLink}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                  onClick={() => copyText(inviteLink)}
                >
                  Copy link
                </button>
                <button
                  type="button"
                  className="text-xs font-medium underline"
                  onClick={() => setInviteLink(null)}
                >
                  Dismiss
                </button>
              </div>
              <p className="mt-2 text-xs text-sky-900/80">
                The full link is only shown once here (or when you create a new
                link). Keep it private.
              </p>
            </div>
          )}

          {resetDone && (
            <div
              className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
              role="status"
            >
              <p className="font-semibold">
                Password reset for {resetDone.full_name} ({resetDone.email})
              </p>
              <p className="mt-1">
                Temporary password:{' '}
                <code className="rounded bg-white px-1.5 py-0.5 font-mono text-brand">
                  {resetDone.password}
                </code>
                <button
                  type="button"
                  className="ml-2 text-xs font-medium underline"
                  onClick={() => copyText(resetDone.password)}
                >
                  Copy
                </button>
              </p>
              <p className="mt-1 text-xs text-emerald-800">
                They will be asked to choose a new password when they next sign in.
              </p>
              <button
                type="button"
                className="mt-2 text-xs font-medium underline"
                onClick={() => setResetDone(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          {resetUser && (
            <form
              onSubmit={confirmResetPassword}
              className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-white/90 p-5 shadow-sm"
            >
              <p className="text-sm font-semibold text-brand">
                Reset password — {resetUser.full_name} ({resetUser.email})
              </p>
              <p className="text-xs text-brand-muted">
                Set a temporary password and share it with the user securely. They
                must change it on next sign-in.
              </p>
              <label className="block max-w-md text-sm">
                <span className="text-brand-muted">Temporary password</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  <input
                    type="text"
                    required
                    minLength={8}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="min-w-[12rem] flex-1 rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                    autoComplete="new-password"
                    disabled={busy}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setResetPassword(generateTemporaryPassword())}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-brand hover:bg-slate-50"
                  >
                    Generate
                  </button>
                  <button
                    type="button"
                    disabled={busy || !resetPassword}
                    onClick={() => copyText(resetPassword)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-brand hover:bg-slate-50"
                  >
                    Copy
                  </button>
                </div>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={busy || resetPassword.trim().length < 8}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Confirm reset'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={cancelResetPassword}
                  className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-brand hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <form
            onSubmit={handleInvite}
            className="mt-8 space-y-3 rounded-lg border border-slate-200 bg-white/90 p-5 shadow-sm"
          >
            <p className="text-sm font-semibold text-brand">Invite user</p>
            <p className="text-xs text-brand-muted">
              No temporary password needed. They open the link, set their name and
              password, then sign in.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block text-sm">
                <span className="text-brand-muted">Email</span>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">First name (optional)</span>
                <input
                  value={inviteForm.first_name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, first_name: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Surname (optional)</span>
                <input
                  value={inviteForm.surname}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, surname: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Role</span>
                <select
                  value={inviteForm.role}
                  onChange={(e) =>
                    setInviteForm({
                      ...inviteForm,
                      role: e.target.value,
                      can_edit:
                        e.target.value === 'admin' ? true : inviteForm.can_edit,
                    })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Link expires (days)</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={inviteForm.expires_in_days}
                  onChange={(e) =>
                    setInviteForm({
                      ...inviteForm,
                      expires_in_days: e.target.value,
                    })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              {inviteForm.role === 'customer' && (
                <div className="block text-sm sm:col-span-2">
                  <span className="text-brand-muted">Customers (one or more)</span>
                  <div className="mt-1">
                    <CustomerPicker
                      customers={customers}
                      selectedIds={inviteForm.customer_ids}
                      onChange={(ids) =>
                        setInviteForm({ ...inviteForm, customer_ids: ids })
                      }
                      disabled={busy}
                    />
                  </div>
                </div>
              )}
            </div>
            {inviteForm.role === 'customer' && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-3 text-sm text-brand">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={inviteForm.can_edit}
                    onChange={(e) =>
                      setInviteForm({
                        ...inviteForm,
                        can_edit: e.target.checked,
                      })
                    }
                  />
                  Can edit knowledge base
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={inviteForm.can_view_secrets}
                    onChange={(e) =>
                      setInviteForm({
                        ...inviteForm,
                        can_view_secrets: e.target.checked,
                      })
                    }
                  />
                  Can view passwords
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={inviteForm.can_use_ai_search}
                    onChange={(e) =>
                      setInviteForm({
                        ...inviteForm,
                        can_use_ai_search: e.target.checked,
                      })
                    }
                  />
                  Can use AI Search
                </label>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create invite link'}
            </button>
          </form>

          {invites.length > 0 && (
            <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white/90 shadow-sm">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-brand">Pending invites</p>
              </div>
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-brand-muted">
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Role</th>
                    <th className="px-4 py-2 font-medium">Customers</th>
                    <th className="px-4 py-2 font-medium">Expires</th>
                    <th className="px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100">
                      <td className="px-4 py-2.5">{inv.email}</td>
                      <td className="px-4 py-2.5 capitalize">{inv.role}</td>
                      <td className="px-4 py-2.5 text-xs text-brand-muted">
                        {inv.role === 'admin'
                          ? '—'
                          : (inv.customer_names || []).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs">
                        {new Date(inv.expires_at).toLocaleString('en-GB')}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs font-medium underline"
                            onClick={() => handleRegenerateInvite(inv.id)}
                          >
                            New link
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            className="text-xs font-medium text-red-700 underline"
                            onClick={() => handleRevokeInvite(inv.id)}
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form
            onSubmit={handleCreate}
            className="mt-8 space-y-3 rounded-lg border border-slate-200 bg-white/90 p-5 shadow-sm"
          >
            <p className="text-sm font-semibold text-brand">
              Create with temporary password
            </p>
            <p className="text-xs text-brand-muted">
              Use when you need the account immediately and will share a temporary
              password yourself.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block text-sm">
                <span className="text-brand-muted">Email</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">First name</span>
                <input
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Surname</span>
                <input
                  required
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Temporary password</span>
                <input
                  type="text"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                  autoComplete="new-password"
                />
              </label>
              <label className="block text-sm">
                <span className="text-brand-muted">Role</span>
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      role: e.target.value,
                      can_edit: e.target.value === 'admin' ? true : form.can_edit,
                    })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              {form.role === 'customer' && (
                <div className="block text-sm sm:col-span-2 lg:col-span-3">
                  <span className="text-brand-muted">Customers (one or more)</span>
                  <div className="mt-1">
                    <CustomerPicker
                      customers={customers}
                      selectedIds={form.customer_ids}
                      onChange={(ids) => setForm({ ...form, customer_ids: ids })}
                      disabled={busy}
                    />
                  </div>
                </div>
              )}
            </div>
            {form.role !== 'admin' && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-3 text-sm text-brand">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.can_edit}
                    onChange={(e) =>
                      setForm({ ...form, can_edit: e.target.checked })
                    }
                  />
                  Can edit knowledge base
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.can_view_secrets}
                    onChange={(e) =>
                      setForm({ ...form, can_view_secrets: e.target.checked })
                    }
                  />
                  Can view passwords
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.can_use_ai_search}
                    onChange={(e) =>
                      setForm({ ...form, can_use_ai_search: e.target.checked })
                    }
                  />
                  Can use AI Search
                </label>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              Create user
            </button>
          </form>

          {loading ? (
            <p className="mt-8 text-brand-muted">Loading…</p>
          ) : (
            <div className="mt-8 overflow-x-auto rounded-lg border border-slate-200 bg-white/90 shadow-sm">
              <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                <thead className="bg-slate-50 text-brand-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Customers</th>
                    <th className="px-4 py-3 font-medium">Edit</th>
                    <th className="px-4 py-3 font-medium">Passwords</th>
                    <th className="px-4 py-3 font-medium">AI Search</th>
                    <th className="px-4 py-3 font-medium">2FA</th>
                    <th className="px-4 py-3 font-medium">Last login</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'opacity-60' : undefined}>
                      <td className="px-4 py-3 align-top">
                        {editId === u.id ? (
                          <form className="space-y-2" onSubmit={handleSaveEdit}>
                            <input
                              value={edit.first_name}
                              onChange={(e) =>
                                setEdit({ ...edit, first_name: e.target.value })
                              }
                              placeholder="First name"
                              className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                            <input
                              value={edit.surname}
                              onChange={(e) =>
                                setEdit({ ...edit, surname: e.target.value })
                              }
                              placeholder="Surname"
                              className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                            <p className="text-xs text-brand-muted">{u.email}</p>
                            <input
                              type="text"
                              placeholder="New temporary password (optional)"
                              value={edit.password}
                              onChange={(e) =>
                                setEdit({ ...edit, password: e.target.value })
                              }
                              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                              autoComplete="new-password"
                            />
                            <div className="flex flex-wrap gap-2">
                              <select
                                value={edit.role}
                                onChange={(e) =>
                                  setEdit({ ...edit, role: e.target.value })
                                }
                                className="rounded border border-slate-300 px-2 py-1 text-xs"
                              >
                                {ROLES.map((r) => (
                                  <option key={r.value} value={r.value}>
                                    {r.label}
                                  </option>
                                ))}
                              </select>
                              {edit.role === 'customer' && (
                                <div className="w-full space-y-2">
                                  <div>
                                    <p className="mb-1 text-xs text-brand-muted">
                                      Customers
                                    </p>
                                    <CustomerPicker
                                      customers={customers}
                                      selectedIds={edit.customer_ids}
                                      onChange={(ids) =>
                                        setEdit({ ...edit, customer_ids: ids })
                                      }
                                      disabled={busy}
                                    />
                                  </div>
                                  <label className="flex items-center gap-2 text-xs text-brand">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(edit.can_edit)}
                                      onChange={(e) =>
                                        setEdit({ ...edit, can_edit: e.target.checked })
                                      }
                                    />
                                    Can edit knowledge base
                                  </label>
                                  <label className="flex items-center gap-2 text-xs text-brand">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(edit.can_view_secrets)}
                                      onChange={(e) =>
                                        setEdit({
                                          ...edit,
                                          can_view_secrets: e.target.checked,
                                        })
                                      }
                                    />
                                    Can view passwords
                                  </label>
                                  <label className="flex items-center gap-2 text-xs text-brand">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(edit.can_use_ai_search)}
                                      onChange={(e) =>
                                        setEdit({
                                          ...edit,
                                          can_use_ai_search: e.target.checked,
                                        })
                                      }
                                    />
                                    Can use AI Search
                                  </label>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={busy}
                                className="rounded bg-brand px-2 py-1 text-xs text-white hover:bg-slate-800"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="text-xs text-brand-muted"
                                onClick={() => {
                                  setEditId(null)
                                  setEdit(null)
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <p className="font-medium text-brand">{u.full_name}</p>
                            <p className="text-xs text-brand-muted">{u.email}</p>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top capitalize">{u.role}</td>
                      <td className="px-4 py-3 align-top text-brand-muted">
                        {customerNames(
                          u.customer_ids?.length
                            ? u.customer_ids
                            : u.customer_id != null
                              ? [u.customer_id]
                              : [],
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.role === 'admin' ? (
                          <span className="text-xs text-brand-muted">Always</span>
                        ) : (
                          <button
                            type="button"
                            disabled={busy || editId === u.id}
                            onClick={() => toggleCanEdit(u)}
                            className="text-xs font-medium text-brand underline disabled:opacity-40"
                          >
                            {u.can_edit ? 'Yes — revoke' : 'No — grant'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.role === 'admin' ? (
                          <span className="text-xs text-brand-muted">Always</span>
                        ) : (
                          <button
                            type="button"
                            disabled={busy || editId === u.id}
                            onClick={() => toggleCanViewSecrets(u)}
                            className="text-xs font-medium text-brand underline disabled:opacity-40"
                          >
                            {u.can_view_secrets ? 'Yes — revoke' : 'No — grant'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {u.role === 'admin' ? (
                          <span className="text-xs text-brand-muted">Always</span>
                        ) : (
                          <button
                            type="button"
                            disabled={busy || editId === u.id}
                            onClick={() => toggleCanUseAiSearch(u)}
                            className="text-xs font-medium text-brand underline disabled:opacity-40"
                          >
                            {u.can_use_ai_search ? 'Yes — revoke' : 'No — grant'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs">
                        {u.totp_enabled ? (
                          <span>
                            Enrolled
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => turnOff2fa(u)}
                              className="ml-2 font-medium text-brand underline disabled:opacity-40"
                            >
                              Turn off
                            </button>
                          </span>
                        ) : u.mfa_exempt ? (
                          <span>
                            Off (testing)
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => require2faAgain(u)}
                              className="ml-2 font-medium text-brand underline disabled:opacity-40"
                            >
                              Require again
                            </button>
                          </span>
                        ) : (
                          <span>
                            {u.require_mfa ? 'Required' : 'Off'}
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => turnOff2fa(u)}
                              className="ml-2 font-medium text-brand underline disabled:opacity-40"
                            >
                              Turn off
                            </button>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-brand-muted">
                        {u.last_login
                          ? new Date(u.last_login).toLocaleString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3 align-top text-xs">
                        {u.is_active ? 'Active' : 'Inactive'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {editId !== u.id && (
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              className="text-left text-xs font-medium text-brand underline"
                              onClick={() => startEdit(u)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="text-left text-xs font-medium text-brand underline disabled:opacity-40"
                              onClick={() => startResetPassword(u)}
                            >
                              Reset password
                            </button>
                            {u.id !== me?.id && (
                              <>
                                <button
                                  type="button"
                                  disabled={busy}
                                  className="text-left text-xs text-brand-muted underline disabled:opacity-40"
                                  onClick={() => toggleActive(u)}
                                >
                                  {u.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  className="text-left text-xs text-red-700 underline disabled:opacity-40"
                                  onClick={() => handleDelete(u)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </SettingsShell>
  )
}
